/**
 * The novelty panel and the week-6 verdict (TASKS T73, SPEC §8.7, RESEARCH §4).
 *
 * > The novelty effect is a genuine unresolved caveat — short gamified interventions may be
 * > exploiting wear-off. CONTESTED. Consequence: telemetry must make weeks 1-6 comparable so decay
 * > is visible rather than assumed away.
 *
 * This module exists because the evidence behind the whole design might be measuring novelty. The
 * honest response to that is not to argue; it is to plot the thing that would show it, and to write
 * down in advance what result would mean the design was wrong.
 *
 * Two rules, and both are refusals:
 *
 *  - **By week, weeks 1 to 12, and no trend line.** No projection, no fitted curve, no
 *    interpretation. A trend line drawn through six noisy points is a claim, and the claim would be
 *    exactly the one under dispute.
 *  - **The week-6 card states a verdict, and does not soften it.** If the game fails its own
 *    criteria it says the design was wrong, and it does not offer to fix it in the same card
 *    (SPEC §8.7). An app that failed its own test and immediately proposed a remedy would be
 *    negotiating with its own evidence.
 *
 * Pure. No clock, no store, no DOM.
 */

import type { Attempt, SessionRecord } from '../store/schema';
import { DAY_MS } from './metrics';
import { advancesInterval } from '../scheduler/gate';

/** The panel's x-axis (SPEC §8.7). */
export const FIRST_WEEK = 1;
export const LAST_WEEK = 12;

/** The week the verdict card is drawn. */
export const VERDICT_WEEK = 6;

/** Production share below this is one of the three kill criteria. */
export const PRODUCTION_FLOOR = 0.6;

const WEEK_MS = 7 * DAY_MS;

/** Which week since first launch a moment falls in. Week 1 is the first seven days. */
export function weekOf(firstLaunchAt: number, at: number): number {
  return Math.floor((at - firstLaunchAt) / WEEK_MS) + 1;
}

export interface WeekPoint {
  week: number;
  /** Null where the week has nothing in it — never 0, which would draw a line to the floor. */
  retention: number | null;
  medianMinutes: number | null;
  sessions: number | null;
}

export interface NoveltyInput {
  attempts: readonly Attempt[];
  sessions: readonly SessionRecord[];
  firstLaunchAt: number;
  now: number;
}

/**
 * Weeks 1 to 12, as points.
 *
 * A week the learner has not reached yet is **absent**, not zero: a chart that drew future weeks at
 * the floor would show a collapse that has not happened. A week he lived through and did nothing in
 * is present with `null` figures, which is a different fact and worth seeing.
 */
export function noveltySeries(input: NoveltyInput): WeekPoint[] {
  const reached = Math.min(LAST_WEEK, weekOf(input.firstLaunchAt, input.now));
  const points: WeekPoint[] = [];

  for (let week = FIRST_WEEK; week <= reached; week += 1) {
    const from = input.firstLaunchAt + (week - 1) * WEEK_MS;
    const to = from + WEEK_MS;

    const qualifying = input.attempts.filter(
      (attempt) =>
        attempt.at >= from && attempt.at < to && attempt.wasDueReview && advancesInterval(attempt),
    );
    const played = input.sessions.filter(
      (session) => session.startedAt >= from && session.startedAt < to,
    );
    const lengths = played.map((session) => session.appMeasuredMinutes).sort((a, b) => a - b);

    points.push({
      week,
      retention:
        qualifying.length === 0
          ? null
          : qualifying.filter((attempt) => attempt.correct).length / qualifying.length,
      medianMinutes: lengths.length === 0 ? null : median(lengths),
      sessions: played.length === 0 ? null : played.length,
    });
  }

  return points;
}

function median(sorted: readonly number[]): number {
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
}

// ---------------------------------------------------------------------------------------------
// The week-6 verdict
// ---------------------------------------------------------------------------------------------

export interface Criterion {
  label: string;
  /** What was measured, and what it is read against. Both, always. */
  value: string;
  against: string;
  /** True when this criterion was met. Null when the baseline needed to judge it is missing. */
  passed: boolean | null;
  /**
   * This criterion is a comparison with the **pre-game baseline**.
   *
   * The verdict's headline claim is "better than what you were doing before", and only these two can
   * support it. The production-share floor is a fixed threshold, not a comparison — see the note in
   * `weekSixVerdict` on why that distinction changed the verdict.
   */
  comparesToBaseline: boolean;
}

export interface Verdict {
  /** Week 6 has not arrived yet. */
  available: boolean;
  criteria: Criterion[];
  /**
   * The sentence. Either the design held up, or it did not and this says so.
   *
   * Null before week 6. Never softened, and never accompanied by an offer to fix anything.
   */
  statement: string | null;
  /** `null` when a baseline was never captured and the comparison cannot honestly be made. */
  failed: boolean | null;
}

export interface VerdictInput {
  /** Retention over the trailing 28 days, at week 6. */
  retention: number | null;
  medianDailyMinutes: number | null;
  /** Production attempts over recognition. Structurally 1 in this app — see below. */
  productionShare: number | null;
  baseline: { retentionPct: number | null; dailyMinutes: number | null };
  week: number;
}

/**
 * The game's own kill criteria, at week 6 (SPEC §8.7).
 *
 * Read against the **pre-game baseline he entered at setup** (T46b), which is why that card asks at
 * all: by week 6 the pre-game numbers are unrecoverable, and a verdict with nothing to compare
 * against is a verdict about nothing. Where he answered "not known", the criterion reports that it
 * cannot be judged rather than quietly passing — a missing baseline must never make the game's own
 * test easier.
 */
export function weekSixVerdict(input: VerdictInput): Verdict {
  if (input.week < VERDICT_WEEK) {
    return { available: false, criteria: [], statement: null, failed: null };
  }

  const criteria: Criterion[] = [
    compare(
      'Retention against your baseline',
      input.retention === null ? null : Math.round(input.retention * 100),
      input.baseline.retentionPct,
      (value) => `${value}%`,
    ),
    compare(
      'Minutes a day against your baseline',
      input.medianDailyMinutes === null ? null : Math.round(input.medianDailyMinutes),
      input.baseline.dailyMinutes,
      (value) => `${value} min`,
    ),
    {
      label: 'Production share',
      value: input.productionShare === null ? 'not measured' : `${Math.round(input.productionShare * 100)}%`,
      against: `${Math.round(PRODUCTION_FLOOR * 100)}%`,
      passed: input.productionShare === null ? null : input.productionShare >= PRODUCTION_FLOOR,
      // A fixed floor, not a comparison with anything he did before.
      comparesToBaseline: false,
    },
  ];

  const judged = criteria.filter((criterion) => criterion.passed !== null);
  const failed = judged.some((criterion) => criterion.passed === false);

  /*
   * A failure is a failure however little else could be judged: one criterion falling short is
   * enough, and nothing about a missing baseline excuses it.
   *
   * But the *positive* claim needs the baseline. Production share is a fixed 60% floor, and this app
   * has no recognition mode at all, so it passes structurally — which meant a learner who left the
   * baseline blank at setup was told "this game has beaten what you were doing before" on the
   * strength of a comparison that had never been made. The one thing this card exists to do is be
   * trustworthy when it says the design was wrong; it cannot be that if it is glib when it says the
   * opposite. See DECISIONS D-233.
   */
  const comparable = judged.some((criterion) => criterion.comparesToBaseline);
  if (!failed && !comparable) {
    return {
      available: true,
      criteria,
      // Said plainly rather than treated as a pass. The absence of evidence is not evidence.
      statement:
        'There is not enough here to judge the design. No baseline was recorded, so the comparison cannot be made.',
      failed: null,
    };
  }
  return {
    available: true,
    criteria,
    statement: failed
      ? // Not softened, and no remedy offered in the same card (SPEC §8.7).
        'This game has not beaten what you were doing before. On its own criteria, the design was wrong.'
      : 'This game has beaten what you were doing before, on its own criteria.',
    failed,
  };
}

function compare(
  label: string,
  now: number | null,
  baseline: number | null,
  format: (value: number) => string,
): Criterion {
  return {
    label,
    comparesToBaseline: true,
    value: now === null ? 'not measured' : format(now),
    against: baseline === null ? 'not known' : format(baseline),
    // Both halves are needed. Either missing means the criterion cannot be judged, and saying so is
    // the honest outcome — a missing baseline must not make the test easier to pass.
    passed: now === null || baseline === null ? null : now >= baseline,
  };
}

// ---------------------------------------------------------------------------------------------
// The proficiency checkpoints (T74, SPEC §8.6)
// ---------------------------------------------------------------------------------------------

/**
 * The checkpoint calendar.
 *
 * A calendar, and nothing more. **No CEFR letter is ever displayed as if measured** — the app cannot
 * assess CEFR and says so — so these are dates with a proxy beside them, and the label carries the
 * word "target" every time it is shown.
 */
export const CHECKPOINTS: readonly { label: string; months: number }[] = Object.freeze([
  { label: 'A2 target', months: 6 },
  { label: 'B1 target', months: 12 },
  { label: 'B2 target', months: 24 },
]);

export type DiagnosisKey = 'output' | 'lapsed' | 'optimistic';

export interface Diagnosis {
  key: DiagnosisKey;
  /** In the order SPEC §8.6 fixes. The order is the diagnostic. */
  order: number;
  statement: string;
  /** Whether the data actually supports this one. */
  supported: boolean;
  /** The figures it was read from, so the claim can be checked. */
  evidence: string;
}

export interface DiagnosisInput {
  /** Balance ratio: input minutes over output minutes. */
  balanceRatio: number | null;
  conversationsLogged: number;
  retention: number | null;
  /** Days in the trailing window with no session at all. */
  daysWithoutReviews: number;
  windowDays: number;
  manualMinutes: number;
  measuredMinutes: number;
}

/**
 * The three diagnoses, in order, with which the data supports (SPEC §8.6).
 *
 * It says nothing about effort or discipline. Each is a statement about *the numbers*, and the third
 * exists specifically to catch the case where the numbers themselves were optimistic — comparing
 * what he logged against what the app measured, and showing both.
 */
export function diagnose(input: DiagnosisInput): Diagnosis[] {
  return [
    {
      key: 'output',
      order: 1,
      statement: 'Not enough output practice.',
      // More input than output, or almost no conversation.
      supported:
        (input.balanceRatio !== null && input.balanceRatio > 1.5) || input.conversationsLogged < 2,
      evidence: `balance ${
        input.balanceRatio === null ? 'not measured' : `${input.balanceRatio.toFixed(1)} : 1`
      } · ${input.conversationsLogged} conversations logged`,
    },
    {
      key: 'lapsed',
      order: 2,
      statement: 'The review queue lapsed.',
      supported:
        (input.retention !== null && input.retention < 0.8) ||
        input.daysWithoutReviews > input.windowDays / 2,
      evidence: `retention ${
        input.retention === null ? 'not measured' : `${Math.round(input.retention * 100)}%`
      } · ${input.daysWithoutReviews} of ${input.windowDays} days with no session`,
    },
    {
      key: 'optimistic',
      order: 3,
      statement: 'The logged hours were optimistic.',
      // Logged far more than was measured, over the same period.
      supported: input.measuredMinutes > 0 && input.manualMinutes > input.measuredMinutes * 3,
      // Both, side by side, and never summed (DoD-12).
      evidence: `${Math.round(input.manualMinutes)} min logged · ${Math.round(
        input.measuredMinutes,
      )} min measured`,
    },
  ];
}
